// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { SendAndConfirmOptions as RpcSendAndConfirmOptions } from "@rialo/ts-cdk";
import type {
  RialoSignAndSendTransactionFeature,
  RialoSignTransactionFeature,
} from "@rialo/wallet-standard";
import type { IdentifierString, Wallet } from "@wallet-standard/base";
import type { FrostConfig } from "../config";
import {
  isFrostError,
  TransactionFailedError,
  UnsupportedFeatureError,
  WalletDisconnectedError,
  WalletError,
  WalletNotFoundError,
} from "../errors";

type WalletWithTransactionFeatures = Wallet & {
  features: {
    "rialo:signTransaction"?: RialoSignTransactionFeature["rialo:signTransaction"];
    "rialo:signAndSendTransaction"?: RialoSignAndSendTransactionFeature["rialo:signAndSendTransaction"];
  };
};

export interface SignTransactionOptions {
  /** Transaction bytes or object with serialize method */
  transaction: Uint8Array | { serialize(): Uint8Array };
}

export interface SignTransactionResult {
  /** Fully signed transaction bytes */
  signedTransaction: Uint8Array;
}

/**
 * Signs a transaction without submitting it.
 *
 * @param config - Frost configuration
 * @param options - Transaction to sign
 * @returns Signed transaction bytes
 * @throws {WalletDisconnectedError} If no wallet connected
 * @throws {UnsupportedFeatureError} If wallet doesn't support signing
 */
export async function signTransaction(
  config: FrostConfig,
  options: SignTransactionOptions
): Promise<SignTransactionResult> {
  const { store } = config;
  const { walletName, accountAddress, chainId } = store.state;

  if (!walletName || !accountAddress) {
    throw new WalletDisconnectedError();
  }

  const wallet = store.state._walletRefs.get(walletName) as
    | WalletWithTransactionFeatures
    | undefined;
  if (!wallet) {
    throw new WalletNotFoundError(walletName);
  }

  const signFeature = wallet.features["rialo:signTransaction"];
  if (!signFeature) {
    throw new UnsupportedFeatureError("rialo:signTransaction", walletName);
  }

  const txBytes =
    "serialize" in options.transaction ? options.transaction.serialize() : options.transaction;

  // Get the account entity with raw wallet-standard reference
  const account = store.state.accounts.get(accountAddress);
  if (!account?._raw) {
    throw new WalletDisconnectedError();
  }

  try {
    const results = await signFeature.signTransaction({
      account: account._raw,
      transaction: txBytes,
      chain: chainId as IdentifierString,
    });

    // Update last used timestamp
    store.setState(state => ({
      ...state,
      lastUsedAt: Date.now(),
    }));

    return {
      signedTransaction: results[0].signedTransaction,
    };
  } catch (error) {
    if (isFrostError(error)) {
      throw error;
    }
    throw new WalletError(error, walletName);
  }
}

export interface SendTransactionType {
  /** Transaction bytes or object with serialize method */
  transaction: Uint8Array | { serialize(): Uint8Array };
}

export interface SendTransactionResult {
  /** Transaction signature */
  signature: Uint8Array;
}

/**
 * Signs and sends a transaction using the wallet's built-in send feature.
 *
 * @param config - Frost configuration
 * @param options - Transaction to sign and send
 * @returns Transaction signature
 * @throws {WalletDisconnectedError} If no wallet connected
 * @throws {UnsupportedFeatureError} If wallet doesn't support signAndSend
 */
export async function sendTransaction(
  config: FrostConfig,
  options: SendTransactionType
): Promise<SendTransactionResult> {
  const { store } = config;
  const { walletName, accountAddress, chainId } = store.state;

  if (!walletName || !accountAddress) {
    throw new WalletDisconnectedError();
  }

  const wallet = store.state._walletRefs.get(walletName) as
    | WalletWithTransactionFeatures
    | undefined;
  if (!wallet) {
    throw new WalletNotFoundError(walletName);
  }

  const sendFeature = wallet.features["rialo:signAndSendTransaction"];
  if (!sendFeature) {
    throw new UnsupportedFeatureError("rialo:signAndSendTransaction", walletName);
  }

  const txBytes =
    "serialize" in options.transaction ? options.transaction.serialize() : options.transaction;

  // Get the account entity with raw wallet-standard reference
  const account = store.state.accounts.get(accountAddress);
  if (!account?._raw) {
    throw new WalletDisconnectedError();
  }

  try {
    const results = await sendFeature.signAndSendTransaction({
      account: account._raw,
      transaction: txBytes,
      chain: chainId as IdentifierString,
    });

    // Update last used timestamp
    store.setState(state => ({
      ...state,
      lastUsedAt: Date.now(),
    }));

    return {
      signature: results[0].signature,
    };
  } catch (error) {
    if (isFrostError(error)) {
      throw error;
    }
    throw new WalletError(error, walletName);
  }
}

export interface SignAndSendTransactionOptions {
  /** Transaction bytes or object with serialize method */
  transaction: Uint8Array | { serialize(): Uint8Array };
  /** RPC send and confirm options (skipPreflight, maxRetries, confirmMaxRetries, confirmRetryDelayMs) */
  sendOptions?: RpcSendAndConfirmOptions;
}

export interface SignAndSendTransactionResult {
  /** Transaction signature (base58 string) */
  signature: string;
}

/**
 * Sign a transaction with the wallet, send it via Frost's RPC client, and wait for confirmation.
 *
 * This provides more control over transaction submission compared to sendTransaction,
 * as it uses Frost's own RPC client instead of the wallet's built-in send.
 * This is useful when you want to:
 * - Use a custom RPC URL (configured via ChainDefinition.rpcUrl)
 * - Have more control over retry logic and confirmation
 * - Not trust the wallet's send implementation
 *
 * @returns Confirmed transaction details including signature and execution status
 */
export async function signAndSendTransaction(
  config: FrostConfig,
  options: SignAndSendTransactionOptions
): Promise<SignAndSendTransactionResult> {
  // First, sign the transaction using the wallet
  const { signedTransaction } = await signTransaction(config, {
    transaction: options.transaction,
  });

  // Then send and confirm via Frost's RPC client
  try {
    const result = await config.client.sendAndConfirmTransaction(
      signedTransaction,
      options.sendOptions
    );

    // Update last used timestamp
    config.store.setState(state => ({
      ...state,
      lastUsedAt: Date.now(),
    }));

    // Check if transaction execution failed on-chain
    if (!result.executed) {
      throw new TransactionFailedError(
        result.signature,
        result.err,
        config.store.state.walletName ?? undefined
      );
    }

    return { signature: result.signature };
  } catch (error) {
    if (isFrostError(error)) {
      throw error;
    }
    throw new WalletError(error, config.store.state.walletName ?? "unknown");
  }
}
